import { useRef, useCallback } from 'react';

export const useVoiceRecognition = ({
  showToast,
  setIsVoiceEnabled,
  isChatOpen,
  setIsChatOpen,
  handleChatMessage, // for foreground voice recognition (챗봇과 직접 연동)
  onTranscriptUpdate, // 실시간 텍스트 업데이트 콜백 (선택적)
  onVoiceComplete,   // 음성 인식 완료 콜백 (선택적) - AI 일정 분석 흐름
}) => {
  const foregroundRecognitionRef = useRef(null); // 포그라운드 인식 객체 ref (중지용)

  // 포그라운드 음성 인식 시작 (버튼 클릭 시)
  const handleStartVoiceRecognition = useCallback(async () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      showToast('음성 인식을 지원하지 않습니다.');
      return;
    }

    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      showToast('음성 기능은 HTTPS 환경에서만 사용 가능합니다.');
      return;
    }

    try {
      const permResult = await navigator.permissions.query({ name: 'microphone' });
      if (permResult.state === 'denied') {
        showToast('마이크 권한이 차단되어 있습니다. 브라우저 설정에서 허용해주세요.');
        return;
      }
    } catch (e) {
      // permissions API 미지원 브라우저 → 그냥 진행
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'ko-KR';
    recognition.interimResults = true; // 실시간 중간 결과 활성화

    recognition.onstart = () => {
      setIsVoiceEnabled(true);
      foregroundRecognitionRef.current = recognition;
    };

    recognition.onresult = async (event) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const text = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += text;
        } else {
          interimTranscript += text;
        }
      }

      // 실시간 텍스트 업데이트 (모달 표시용)
      if (onTranscriptUpdate) {
        onTranscriptUpdate(finalTranscript || interimTranscript);
      }

      // 최종 인식 완료 시
      if (finalTranscript) {
        if (onVoiceComplete) {
          // AI 일정 분석 흐름
          recognition.stop();
          onVoiceComplete(finalTranscript);
        } else {
          // 기존 챗봇 흐름
          if (!isChatOpen) setIsChatOpen(true);
          await handleChatMessage(finalTranscript);
        }
      }
    };

    recognition.onerror = (e) => {
      setIsVoiceEnabled(false);
      if (onTranscriptUpdate) onTranscriptUpdate('');
      if (e.error === 'not-allowed') {
        showToast('마이크 권한이 차단되어 있습니다. 브라우저 설정에서 허용해주세요.');
      }
    };

    recognition.onend = () => {
      setIsVoiceEnabled(false);
      foregroundRecognitionRef.current = null;
    };

    recognition.start();
  }, [showToast, setIsVoiceEnabled, isChatOpen, setIsChatOpen, handleChatMessage, onTranscriptUpdate, onVoiceComplete]);

  const handleStopVoiceRecognition = useCallback(() => {
    if (foregroundRecognitionRef.current) {
      foregroundRecognitionRef.current.stop();
      foregroundRecognitionRef.current = null;
      setIsVoiceEnabled(false);
      if (onTranscriptUpdate) onTranscriptUpdate('');
    }
  }, [setIsVoiceEnabled, onTranscriptUpdate]);

  return {
    handleStartVoiceRecognition,
    handleStopVoiceRecognition,
  };
};
